import Button from '@ingka/button';
import Loading, { LoadingBall } from '@ingka/loading';
import ellipsesHorizontal from '@ingka/ssr-icon/paths/ellipses-horizontal';
import pencil from '@ingka/ssr-icon/paths/pencil';
import warn from '@ingka/ssr-icon/paths/warning-triangle';
import Text from '@ingka/text';
import Tooltip from '@ingka/tooltip';
import {
  getLotteryEditPhase,
  isDeleteLotteryAllowed,
  isStopLotteryAllowed,
} from '@lotta/feature-lottery-rules';
import {
  useDeleteLotteryMutation,
  useGetLotteryDrawsQuery,
  useGetLotteryQuery,
  useGetLotteryTranslationsQuery,
  useGetPermissionsQuery,
  useGetRetailUnitQuery,
  useStopLotteryMutation,
} from '@lotta/self-service/lottery-fe/data-access-state';
import {
  useErrorToast,
  useFormatWithLocale,
  useLocale,
  useNavigateWithLocale,
  useStores,
} from '@lotta/self-service/lottery-fe/feature-hooks';
import { LotteryConfigOverview } from '@lotta/self-service/lottery-fe/feature-lottery-config-overview';
import { ManualParticipantsManager } from '@lotta/self-service/lottery-fe/feature-manage-manual-participants';
import { MissingData } from '@lotta/self-service/lottery-fe/ui-common';
import { getUserPermissions } from '@lotta/self-service/lottery-fe/util-utilities';
import { getStoreIdNameDict } from '@lotta/shared/data-access-stores';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styles from './LotteryDetails.module.scss';
import { CompletedDrawsTable } from './components/completed-draws-table/CompletedDrawsTable';
import { OpenDemoButton } from './components/open-demo-button/OpenDemoButton';
import { StopLotteryModal } from './components/stop-lottery-modal/StopLotteryModal';
import { DeleteLotteryModal } from './components/delete-lottery-modal/DeleteLotteryModal';
import { UpcomingDrawsTable } from './components/upcoming-draws-table/UpcomingDrawsTable';
import { EditLotteryModal } from './components/edit-lottery-modal/EditLotteryModal';
import { isSignupBasedLottery } from './utils/is-signup-based-lottery';

/* eslint-disable-next-line */
export interface LotteryDetailsProps {}

export const LotteryDetails: React.FC<LotteryDetailsProps> = () => {
  const { lotteryId } = useParams();
  const navigate = useNavigateWithLocale();
  const { t } = useTranslation();
  const { dateWithLocale } = useFormatWithLocale();
  const { retailUnit, language } = useLocale();
  const {
    data: retailUnitData,
    error: retailUnitError,
    isLoading: retailUnitIsLoading,
  } = useGetRetailUnitQuery({ retailUnit });
  useErrorToast(retailUnitError);
  const { data: permissionData, isLoading: isPermissionsLoading } = useGetPermissionsQuery({});

  const storesData = useStores({ retailUnit, language, retailUnitData });
  useErrorToast(storesData.error);

  const [stopLotteryModalVisible, setStopLotteryModalVisible] = useState(false);
  const [deleteLotteryModalVisible, setDeleteLotteryModalVisible] = useState(false);
  const [manageManualParticipantsModalVisible, setManageManualParticipantsModalVisible] =
    useState(false);
  const [editLotteryModalVisible, setEditLotteryModalVisible] = useState(false);

  const [stopLottery, stopLotteryResult] = useStopLotteryMutation();
  useErrorToast(stopLotteryResult.error, {
    403: 'stop-lottery-error-msg',
    404: 'stop-lottery-error-msg',
  });

  const [deleteLottery, deleteLotteryResult] = useDeleteLotteryMutation();
  useErrorToast(deleteLotteryResult.error, {
    403: 'delete-lottery-error-msg',
    404: 'delete-lottery-error-msg',
  });

  const { data, isLoading, error } = useGetLotteryQuery({
    retailUnit,
    lotteryId: lotteryId ? lotteryId : '',
  });
  useErrorToast(error, { 400: 'lottery-details-not-found-error-msg' });

  const drawDetails = useGetLotteryDrawsQuery({
    retailUnit,
    lotteryId: lotteryId ? lotteryId : '',
  });
  useErrorToast(drawDetails.error);

  const translationsDetails = useGetLotteryTranslationsQuery({
    retailUnit,
    lotteryId: lotteryId ? lotteryId : '',
  });

  useErrorToast(translationsDetails.error);

  const lotteryDraws = drawDetails.currentData?.lotteryDraws ?? [];

  const storesDictionary = storesData.data ? getStoreIdNameDict(storesData.data['stores']) : {};

  let userPermission: string[] = [];

  if (permissionData && !isPermissionsLoading) {
    userPermission = getUserPermissions(retailUnit.toUpperCase(), permissionData.permissions);
  }

  const allowedGroups = ['SUPER_ADMIN', 'ADMIN'];

  if (isLoading || drawDetails.isLoading || translationsDetails.isLoading || retailUnitIsLoading) {
    return (
      <Loading text={`${t('general-fetching-lottery')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (
    !lotteryId ||
    error ||
    !data ||
    drawDetails.error ||
    !translationsDetails.data ||
    retailUnitError ||
    !retailUnitData
  ) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }
  const { lottery } = data;
  const { translations } = translationsDetails.data;

  const values = { lottery, translation: translations };

  const { id } = lottery;

  const handleEditLottery = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    navigate(`/lottery/${id}/edit`);
  };

  const handleCopyLottery = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    navigate(`/lottery/${id}/copy`);
  };

  const handleManageManualParticipants = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setManageManualParticipantsModalVisible(true);
  };

  const handleStop = () => {
    stopLottery({ retailUnit, lotteryId });
    setStopLotteryModalVisible(false);
  };

  const handleDelete = () => {
    deleteLottery({ retailUnit, lotteryId });
    setDeleteLotteryModalVisible(false);
    navigate('/');
  };

  const handleModalEdit = () => {
    setEditLotteryModalVisible(false);
    navigate(`/lottery/${id}/edit`);
  };

  const renderDrawsTable = () => {
    return lotteryDraws.length ? (
      <CompletedDrawsTable completedDraws={lotteryDraws} />
    ) : (
      <MissingData text={t('lottery-details-no-completed-draws')} />
    );
  };

  const now = new Date();

  const lacksPermission = !userPermission.some((role) => allowedGroups.includes(role));
  const editPhase = getLotteryEditPhase(lottery, retailUnitData.retailUnit.timeZone, now);
  const editEnabled = editPhase !== 'CLOSED';
  const editLimited = editPhase === 'LIMITED';

  const editButton = (
    <Button
      small
      id="edit-button"
      type="tertiary"
      disabled={!editEnabled || lacksPermission}
      ssrIcon={pencil}
      iconPosition="leading"
      data-testid="edit-lottery-button"
      onClick={(e) => {
        if (editLimited) {
          setEditLotteryModalVisible(true);
        } else {
          handleEditLottery(e);
        }
      }}
    >
      {t('lottery-details-edit-button')}
    </Button>
  );

  const copyButton = (
    <Button
      small
      id="copy-button"
      type="tertiary"
      disabled={lacksPermission}
      ssrIcon={pencil}
      iconPosition="leading"
      data-testid="copy-lottery-button"
      onClick={(e) => {
        handleCopyLottery(e);
      }}
    >
      {t('lottery-details-copy-button')}
    </Button>
  );

  // Manual participants is not available for swipe-lotteries
  const isManageManualParticipantsVisible = !isSignupBasedLottery(lottery);

  const isManageManualParticipantsEnabled: boolean =
    (lottery.lotteryStatus as string) !== ('COMPLETED' as string) &&
    (lottery.lotteryStatus as string) !== ('STOPPED' as string);

  const manageManualParticipantsButton = (
    <Button
      small
      id="manage-manual-participants-button"
      type="tertiary"
      ssrIcon={ellipsesHorizontal}
      iconPosition="leading"
      data-testid="manage-manual-participants-button"
      onClick={handleManageManualParticipants}
      disabled={!isManageManualParticipantsEnabled || lacksPermission}
    >
      {t('lottery-details-manage-manual-participants')}
    </Button>
  );

  const stopButtonIsEnabled = isStopLotteryAllowed(
    lottery,
    retailUnitData.retailUnit.timeZone,
    now
  );
  const stopButton = (
    <Button
      className={styles['button-filled']}
      small
      id="stop-button"
      text={`${t('lottery-details-stop-button')}`}
      type="primary"
      disabled={!stopButtonIsEnabled || lacksPermission}
      ssrIcon={warn}
      iconPosition="leading"
      data-testid="edit-lottery-button"
      onClick={() => setStopLotteryModalVisible(true)}
    />
  );

  const deleteButtonIsEnabled = isDeleteLotteryAllowed(
    lottery,
    retailUnitData.retailUnit.timeZone,
    new Date()
  );
  const deleteButton = (
    <Button
      className={styles['button-filled']}
      small
      id="delete-button"
      text={`${t('general-delete-lottery')}`}
      type="danger"
      iconPosition="leading"
      data-testid="delete-lottery-button"
      disabled={!deleteButtonIsEnabled || lacksPermission}
      onClick={() => setDeleteLotteryModalVisible(true)}
    />
  );

  return (
    <div className={styles['container']} data-testid="lottery-details-container">
      <Text headingSize="xl" className={styles['header']}>
        {t('lottery-details-header')}
      </Text>
      <div className={styles['buttons-div']}>
        {copyButton}
        <OpenDemoButton lottery={lottery} small type="tertiary" />

        {!editEnabled || lacksPermission ? (
          <Tooltip
            className="tooltip-demo__item"
            interactiveCustomTrigger
            tooltipText={
              !editEnabled
                ? t('lottery-details-edit-button-disabled-description')
                : t('general-insufficient-permissions')
            }
          >
            {editButton}
          </Tooltip>
        ) : (
          editButton
        )}
        {isManageManualParticipantsVisible &&
          (!isManageManualParticipantsEnabled || lacksPermission ? (
            <Tooltip
              className="tooltip-demo__item"
              interactiveCustomTrigger
              tooltipText={
                lottery.lotteryStatus !== 'PENDING'
                  ? t('lottery-details-manage-manual-participants-disabled-tool-tip')
                  : t('general-insufficient-permissions')
              }
            >
              {manageManualParticipantsButton}
            </Tooltip>
          ) : (
            manageManualParticipantsButton
          ))}

        {!stopButtonIsEnabled || lacksPermission ? (
          <Tooltip
            className="tooltip-demo__item"
            interactiveCustomTrigger
            tooltipText={
              !stopButtonIsEnabled
                ? t('lottery-details-stop-button-disabled-tool-tip')
                : t('general-insufficient-permissions')
            }
          >
            {stopButton}
          </Tooltip>
        ) : (
          stopButton
        )}
        {!deleteButtonIsEnabled || lacksPermission ? (
          <Tooltip
            className="tooltip-demo__item"
            interactiveCustomTrigger
            tooltipText={
              !deleteButtonIsEnabled
                ? t('delete-lottery-disabled-description')
                : t('general-insufficient-permissions')
            }
          >
            {deleteButton}
          </Tooltip>
        ) : (
          deleteButton
        )}
      </div>
      <LotteryConfigOverview
        values={values}
        currency={retailUnitData.retailUnit.currency}
        storesDictionary={storesDictionary}
        timezone={retailUnitData.retailUnit.timeZone}
      />
      <div className={styles['text-div']}>
        <Text bodySize="s" className={styles['update-text']}>
          {data.lottery.updatedAt
            ? t('lottery-details-latest-updated', {
                updatedDate: dateWithLocale(data.lottery.updatedAt),
              })
            : ''}
        </Text>
        <hr />
      </div>
      <div className={styles['table-container']}>
        <Text headingSize="m">{t('lottery-details-completed-draws-header')}</Text>
        {renderDrawsTable()}
      </div>
      {lottery.lotteryStatus !== 'COMPLETED' && (
        <div className={styles['table-container']}>
          <Text headingSize="m">{t('lottery-details-upcoming-draws-header')}</Text>
          <UpcomingDrawsTable
            retailUnit={retailUnit}
            lotteryId={lotteryId}
            showSignUps={isSignupBasedLottery(lottery)}
          />
        </div>
      )}
      <StopLotteryModal
        visible={stopLotteryModalVisible}
        onClose={() => {
          setStopLotteryModalVisible(false);
        }}
        onStop={handleStop}
      />
      <DeleteLotteryModal
        visible={deleteLotteryModalVisible}
        onClose={() => {
          setDeleteLotteryModalVisible(false);
        }}
        onDelete={handleDelete}
      />
      <EditLotteryModal
        visible={editLotteryModalVisible}
        onClose={() => {
          setEditLotteryModalVisible(false);
        }}
        onEdit={handleModalEdit}
      />
      <ManualParticipantsManager
        visible={manageManualParticipantsModalVisible}
        onClose={() => {
          setManageManualParticipantsModalVisible(false);
        }}
      />
    </div>
  );
};
